import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { cjSearch } from "../../lib/cj";
import { MarketCard } from "../../components/market/MarketCard";

const SORTS = [
  { v: "popular", l: "Most Popular" },
  { v: "newest", l: "Newest" },
  { v: "price_low", l: "Price: Low to High" },
  { v: "price_high", l: "Price: High to Low" },
];

const MarketSearch = () => {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [sort, setSort] = useState("popular");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const sentinel = useRef(null);

  const load = useCallback(async (p, reset) => {
    setLoading(true);
    try {
      const d = await cjSearch({ q, page: p, size: 24, sort });
      setItems((prev) => (reset ? d.products : [...prev, ...d.products]));
      setPages(d.pages || 1); setCount(d.count || 0);
    } finally { setLoading(false); }
  }, [q, sort]);

  useEffect(() => { setItems([]); setPage(1); load(1, true); }, [load]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && !loading && page < pages) {
        const n = page + 1; setPage(n); load(n, false);
      }
    }, { rootMargin: "400px" });
    io.observe(el);
    return () => io.disconnect();
  }, [page, pages, loading, load]);

  const submit = (e) => { e.preventDefault(); setParams({ q: input }); };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="market-search">
      <form onSubmit={submit} className="flex items-center bg-taven-gray rounded-full h-12 px-5 max-w-2xl mb-6">
        <Search className="w-5 h-5 text-muted-foreground shrink-0" />
        <input data-testid="market-search-input" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Search the TAVEN marketplace…" className="bg-transparent outline-none px-3 w-full text-sm" />
        <button data-testid="market-search-submit" type="submit" className="text-xs font-bold uppercase tracking-wider text-taven-gold">Search</button>
      </form>

      <div className="flex items-center justify-between mb-5">
        <p className="text-sm" data-testid="market-search-count">{q ? <>Results for <span className="font-bold">“{q}”</span></> : "\u00A0"}</p>
        <select data-testid="market-search-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-9 px-3 rounded-md border border-black/10 bg-white text-sm">
          {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {items.map((p) => <MarketCard key={p.taven_product_id || p.handle} product={p} />)}
      </div>
      {items.length === 0 && !loading && <p className="text-sm text-muted-foreground py-12 text-center">No products found.</p>}
      {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
      <div ref={sentinel} className="h-4" />
    </div>
  );
};

export default MarketSearch;
